import { Box, Heading, InfoIcon } from '@wix/design-system';
import { TooltipCommonProps } from '@wix/design-system/dist/types/common';
import { BaseSection } from '@wix/wix-help-widget-common/homepage';
import { useTranslation } from '@wix/wix-i18n-config';
import { Property } from 'csstype';
import React, { FC, useCallback, useState } from 'react';

import { TLocalesKeys } from '../../../types';
import { useMapSectionIdToSectionData } from '../hooks';
import s from './styles.scss';
import { sectionIdToDataHook } from './utils';

export type SectionProps = Readonly<{
  index: number;
  section: BaseSection;
}>;

const tooltipProps: TooltipCommonProps = {
  size: 'medium',
  maxWidth: 300,
};

export const Section: FC<SectionProps> = ({ section, index }) => {
  const { t } = useTranslation();
  const [hidden, setHidden] = useState(false);

  const mapSectionIdToSectionData = useMapSectionIdToSectionData();

  const { SectionComponent, titleKey, titleIcon } = mapSectionIdToSectionData[
    section.sectionId
  ];
  // use custom title if set in content. If not - use default keys
  const titleText = section.title ?? (titleKey && t<TLocalesKeys>(titleKey));
  const display: Property.Display = hidden ? 'none' : 'block';

  const setSectionHidden = useCallback((value) => setHidden(value), []);

  if (!titleText) {
    return (
      <Box
        display={display}
        direction="vertical"
        dataHook={sectionIdToDataHook[section.sectionId]}
      >
        <SectionComponent
          setSectionHidden={setSectionHidden}
          positionIndex={index}
          {...section}
        />
      </Box>
    );
  }

  return (
    <Box
      display={display}
      direction="vertical"
      dataHook={sectionIdToDataHook[section.sectionId]}
    >
      <Box padding="24px 0 12px" gap="8px" verticalAlign="middle">
        <Heading size="small">{titleText}</Heading>
        {titleIcon && (
          <InfoIcon
            className={s.sectionTitleIcon}
            size="small"
            content={t<TLocalesKeys>(titleIcon.tooltipKey)}
            tooltipProps={tooltipProps}
          >
            <titleIcon.Icon fill="#116DFF" />
          </InfoIcon>
        )}
      </Box>
      <Box outline="1px solid #DFE5EB" borderRadius="8px" overflow="hidden">
        <SectionComponent
          setSectionHidden={setSectionHidden}
          positionIndex={index}
          {...section}
        />
      </Box>
    </Box>
  );
};
